import React, { useCallback, useEffect, useState } from "react";
import { api, fmtMoney, fmtDate, formatApiErrorDetail } from "@/lib/apiClient";
import { PageHeader, EmptyState, BcRibbonGroup, BcRibbonBtn } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash, Paperclip, UploadSimple, ArrowsClockwise, FileArrowDown, PencilSimple, CloudArrowDown } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { CsvExport } from "@/components/CsvExport";
import PurchaseRowExpanded from "@/components/ledger/PurchaseRowExpanded";
import { PurchaseUploadSheet } from "@/pages/purchases/PurchaseUploadSheet";
import PurchaseEditSheet from "@/pages/purchases/PurchaseEditSheet";
import EditRowSheet from "@/components/EditRowSheet";

function Field({ label, children }) {
  return <div><Label className="label-caps mb-2 block">{label}</Label>{children}</div>;
}

function Ledger({ kind, testidPrefix, title, subtitle, actorLabel, actorField, resource }) {
  const [rows, setRows] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const empty = {
    project_id: "", [actorField]: "", item: "", quantity: 1, unit: "nos", unit_price: 0,
    date: new Date().toISOString().slice(0, 10), notes: "",
  };
  const [form, setForm] = useState(empty);

  // Purchases use a server-side filtered + paginated feed (handles 1,000s of rows).
  const isPurchase = kind === "purchase";
  const PAGE_SIZE = 50;
  const [page, setPage] = useState(1);
  const [fy, setFy] = useState("");
  const [month, setMonth] = useState("");
  const [vendorF, setVendorF] = useState("");
  const [q, setQ] = useState("");
  const [search, setSearch] = useState("");
  const [totalCount, setTotalCount] = useState(0);
  const [sumTotal, setSumTotal] = useState(0);
  const [sumTax, setSumTax] = useState(0);
  const [facets, setFacets] = useState({ fys: [], vendors: [] });
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [importOpen, setImportOpen] = useState(false);
  const [importing, setImporting] = useState(false);

  const runHistoricalImport = async () => {
    setImporting(true);
    try {
      const { data } = await api.post("/purchases/import-historical");
      toast.success(
        data.inserted > 0
          ? `Imported ${data.inserted.toLocaleString("en-IN")} historical invoices (${data.skipped.toLocaleString("en-IN")} already present). Total now ${data.total_now.toLocaleString("en-IN")}.`
          : `Already up to date — all ${data.file_records.toLocaleString("en-IN")} historical invoices are present.`
      );
      setImportOpen(false);
      setFy(""); setMonth(""); setVendorF(""); setQ(""); setPage(1);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  useEffect(() => { const t = setTimeout(() => { setSearch(q); setPage(1); }, 400); return () => clearTimeout(t); }, [q]);
  useEffect(() => { setPage(1); }, [fy, month, vendorF]);

  const load = useCallback(async () => {
    const projReq = api.get("/projects");
    if (isPurchase) {
      const params = { page, page_size: PAGE_SIZE };
      if (fy) params.fy = fy;
      if (month) params.month = month;
      if (vendorF) params.vendor = vendorF;
      if (search) params.q = search;
      const [a, b] = await Promise.all([api.get("/purchases/paged", { params }), projReq]);
      setRows(a.data.items || []);
      setTotalCount(a.data.total || 0);
      setSumTotal(a.data.sum_total || 0);
      setSumTax(a.data.sum_tax || 0);
      setFacets({ fys: a.data.fys || [], vendors: a.data.vendors || [] });
      setProjects(b.data);
    } else {
      const [a, b] = await Promise.all([api.get(`/${resource}`), projReq]);
      setRows(a.data);
      setProjects(b.data);
    }
  }, [isPurchase, resource, page, fy, month, vendorF, search]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    try {
      await api.post(`/${resource}`, {
        ...form,
        project_id: form.project_id || null,
        quantity: Number(form.quantity), unit_price: Number(form.unit_price),
      });
      toast.success(`${title} saved`);
      setOpen(false);
      setForm(empty);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete?")) return;
    // Purchases support undo via /api/undo/{token}; sales don't yet.
    if (resource === "purchases") {
      const { deleteWithUndo } = await import("@/lib/undoManager");
      await deleteWithUndo(
        () => api.delete(`/${resource}/${id}`),
        { label: `Deleted purchase · ${id.slice(0, 8)}`, onRestored: () => load() },
      );
      load();
      return;
    }
    try { await api.delete(`/${resource}/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const projMap = Object.fromEntries(projects.map((p) => [p.id, p.name]));
  const total = Number(form.quantity || 0) * Number(form.unit_price || 0);
  const totalColor = kind === "sale" ? "text-emerald-700" : "text-red-700";

  const [uploadOpen, setUploadOpen] = useState(false);
  const [editing, setEditing] = useState(null);   // row being edited via EditRowSheet

  // "N" keyboard shortcut → open Upload/New sheet on the current list
  useEffect(() => {
    const handler = () => setUploadOpen(true);
    window.addEventListener("celsius:new", handler);
    return () => window.removeEventListener("celsius:new", handler);
  }, []);

  // Field config for the Sale EditRowSheet (Purchases use the dedicated PurchaseEditSheet)
  const saleEditFields = [
    { name: "customer", label: "Customer", required: true, colSpan: 2 },
    { name: "item", label: "Item / Service", required: true, colSpan: 2 },
    { name: "quantity", label: "Quantity", type: "number" },
    { name: "unit_price", label: "Unit price", type: "number" },
    { name: "date", label: "Date", type: "date", required: true },
    {
      name: "project_id", label: "Project", type: "select",
      options: [{ value: "", label: "— None —" }, ...projects.map((p) => ({ value: p.id, label: p.name }))],
    },
    { name: "notes", label: "Notes", type: "textarea", colSpan: 2 },
  ];

  return (
    <div data-testid={`${testidPrefix}-page`}>
      <PageHeader
        title={title} subtitle={subtitle}
        module={kind === "purchase" ? "Procurement · MM" : "Sales · SD/AR"}
        ribbon={
          <>
            <BcRibbonGroup label="New">
              {kind === "purchase" && (
                <BcRibbonBtn icon={UploadSimple} label="Upload Bill" onClick={() => setUploadOpen(true)} testid="ribbon-upload-bill" />
              )}
              <BcRibbonBtn
                icon={Plus}
                label={`New ${kind === "sale" ? "Sale" : "Purchase"}`}
                variant="primary"
                onClick={() => setOpen(true)}
                testid={`ribbon-new-${testidPrefix}`}
              />
            </BcRibbonGroup>
            <BcRibbonGroup label="Process">
              <BcRibbonBtn icon={ArrowsClockwise} label="Refresh" onClick={load} testid="ribbon-refresh" />
              {kind === "purchase" && isAdmin && (
                <BcRibbonBtn icon={CloudArrowDown} label="Import History" onClick={() => setImportOpen(true)} testid="ribbon-import-history" />
              )}
            </BcRibbonGroup>
            <BcRibbonGroup label="Report">
              <CsvExport resource={resource} render={({ onClick }) => (
                <BcRibbonBtn icon={FileArrowDown} label="Export CSV" onClick={onClick} testid="ribbon-export-csv" />
              )} />
            </BcRibbonGroup>
          </>
        }
      />
      {kind === "purchase" && (
        <PurchaseUploadSheet projects={projects} onUploaded={load} open={uploadOpen} onOpenChange={setUploadOpen} hideTrigger />
      )}

      {/* Edit sheet — Purchase uses full multi-line editor; Sale uses the compact EditRowSheet */}
      {editing && kind === "purchase" && (
        <PurchaseEditSheet
          purchase={editing}
          projects={projects}
          open={!!editing}
          onOpenChange={(v) => !v && setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
      {editing && kind === "sale" && (
        <EditRowSheet
          open={!!editing}
          onOpenChange={(v) => !v && setEditing(null)}
          title="Edit Sale"
          endpoint={`/${resource}/${editing.id}`}
          initial={{ ...editing, project_id: editing.project_id || "" }}
          fields={saleEditFields}
          transformPayload={(f) => ({
            ...f,
            project_id: f.project_id || null,
            quantity: Number(f.quantity || 0),
            unit_price: Number(f.unit_price || 0),
          })}
          onSaved={load}
        />
      )}
      <Sheet open={open} onOpenChange={setOpen}>
            <SheetContent className="w-[440px]">
              <SheetHeader><SheetTitle className="font-display">Record {kind === "sale" ? "Sale" : "Purchase"}</SheetTitle></SheetHeader>
              <div className="space-y-4 mt-6">
                <Field label={actorLabel}><Input value={form[actorField]} data-testid={`${testidPrefix}-actor-input`} onChange={(e) => setForm({ ...form, [actorField]: e.target.value })} /></Field>
                <Field label="Item"><Input value={form.item} onChange={(e) => setForm({ ...form, item: e.target.value })} /></Field>
                <div className="grid grid-cols-3 gap-3">
                  <Field label="Quantity"><Input type="number" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} /></Field>
                  {kind === "purchase" ? (
                    <Field label="Unit">
                      <>
                        <Input list="ledger-unit-options" value={form.unit || "nos"} onChange={(e) => setForm({ ...form, unit: e.target.value })} className="uppercase tracking-wider" placeholder="nos" data-testid={`${testidPrefix}-unit-input`} />
                        <datalist id="ledger-unit-options">
                          <option value="nos" /><option value="pcs" /><option value="pack" /><option value="carton" /><option value="sheet" /><option value="coil" /><option value="set" /><option value="pair" /><option value="roll" /><option value="bundle" /><option value="drum" /><option value="bag" /><option value="box" /><option value="lot" />
                          <option value="kg" /><option value="g" /><option value="ton" />
                          <option value="meter" /><option value="mm" /><option value="cm" /><option value="ft" /><option value="inch" />
                          <option value="sqm" /><option value="sqft" /><option value="cum" /><option value="cuft" />
                          <option value="litre" /><option value="ml" />
                          <option value="hour" /><option value="day" /><option value="week" /><option value="month" />
                        </datalist>
                      </>
                    </Field>
                  ) : null}
                  <Field label="Unit price"><Input type="number" data-testid={`${testidPrefix}-price-input`} value={form.unit_price} onChange={(e) => setForm({ ...form, unit_price: e.target.value })} /></Field>
                </div>
                <div className="bg-slate-50 border border-slate-200 p-3 rounded-sm text-sm">
                  <div className="flex justify-between font-mono-num">
                    <span className="label-caps">Total</span>
                    <span className={`font-semibold ${totalColor}`}>{fmtMoney(total)}</span>
                  </div>
                </div>
                <Field label="Date"><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></Field>
                <Field label="Project (optional)">
                  <Select value={form.project_id || "__none__"} onValueChange={(v) => setForm({ ...form, project_id: v === "__none__" ? "" : v })}>
                    <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">None</SelectItem>
                      {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Notes"><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
                <Button onClick={submit} data-testid={`save-${testidPrefix}-button`} className="w-full rounded-sm bg-[#EA580C] hover:bg-[#c2410c] text-white">Save</Button>
              </div>
            </SheetContent>
          </Sheet>

      <div className="p-6">
        {isPurchase && (
          <div className="mb-4 space-y-3" data-testid="purchases-filter-bar">
            <div className="flex flex-wrap items-end gap-3">
              <div className="w-40">
                <Label className="label-caps mb-1.5 block">Financial Year</Label>
                <Select value={fy || "__all__"} onValueChange={(v) => setFy(v === "__all__" ? "" : v)}>
                  <SelectTrigger data-testid="purchases-fy-select"><SelectValue placeholder="All years" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All years</SelectItem>
                    {facets.fys.map((y) => <SelectItem key={y} value={y}>FY {y}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-40">
                <Label className="label-caps mb-1.5 block">Month</Label>
                <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="purchases-month-input" />
              </div>
              <div className="w-56">
                <Label className="label-caps mb-1.5 block">Vendor</Label>
                <Input list="purchases-vendor-options" value={vendorF} onChange={(e) => setVendorF(e.target.value)} placeholder="All vendors" data-testid="purchases-vendor-input" />
                <datalist id="purchases-vendor-options">
                  {facets.vendors.map((v) => <option key={v} value={v} />)}
                </datalist>
              </div>
              <div className="flex-1 min-w-[180px]">
                <Label className="label-caps mb-1.5 block">Search</Label>
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Bill #, item or vendor…" data-testid="purchases-search-input" />
              </div>
              {(fy || month || vendorF || q) && (
                <Button variant="outline" className="rounded-sm" onClick={() => { setFy(""); setMonth(""); setVendorF(""); setQ(""); }} data-testid="purchases-clear-filters">Clear</Button>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-[12px] text-slate-600" data-testid="purchases-summary">
              <span><span className="font-semibold text-slate-900">{totalCount.toLocaleString("en-IN")}</span> invoices</span>
              <span>Total <span className="font-semibold text-slate-900 font-mono-num">{fmtMoney(sumTotal)}</span></span>
              <span>GST <span className="font-semibold text-slate-900 font-mono-num">{fmtMoney(sumTax)}</span></span>
            </div>
          </div>
        )}
        {rows.length === 0 ? (
          <EmptyState title={isPurchase && (fy || month || vendorF || search) ? "No matching purchases" : `No ${kind === "sale" ? "sales" : "purchases"} yet`} description={isPurchase && (fy || month || vendorF || search) ? "Try clearing filters or adjusting your search." : `Track every ${kind === "sale" ? "sale" : "vendor purchase"} for accurate profit calculation.`} />
        ) : (
          <div className="bc-card overflow-hidden" data-testid={`${testidPrefix}-table`}>
            <Table className="bc-list">
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>{actorLabel}</TableHead>
                  {kind === "purchase" && <TableHead>Bill #</TableHead>}
                  <TableHead>Item</TableHead>
                  <TableHead>Project</TableHead>
                  {kind === "purchase" ? (
                    <>
                      <TableHead className="text-right">Lines</TableHead>
                      <TableHead className="text-right">Subtotal</TableHead>
                      <TableHead className="text-right">GST</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                    </>
                  ) : (
                    <>
                      <TableHead className="text-right">Qty</TableHead>
                      <TableHead className="text-right">Unit</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                    </>
                  )}
                  <TableHead className="w-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => {
                  const lineCount = kind === "purchase" ? (Array.isArray(r.items) ? r.items.length : 1) : 1;
                  const primaryItem = kind === "purchase" && lineCount > 1
                    ? `${r.items[0]?.description || r.item} + ${lineCount - 1} more`
                    : r.item;
                  return (
                  <React.Fragment key={r.id}>
                    <TableRow>
                      <TableCell className="text-slate-600">{fmtDate(r.date)}</TableCell>
                      <TableCell className="font-medium">{r[actorField]}</TableCell>
                      {kind === "purchase" && (
                        <TableCell className="text-slate-600 font-mono-num text-[11.5px]">{r.invoice_number || "—"}</TableCell>
                      )}
                      <TableCell className="text-slate-600">
                        <div className="flex items-center gap-1.5">
                          <span>{primaryItem}</span>
                          {kind === "purchase" && r.destination === "warehouse" && (
                            <span className="inline-flex items-center rounded-sm bg-orange-50 text-orange-700 border border-orange-200 px-1.5 py-[1px] text-[10px] font-semibold uppercase tracking-widest">WH</span>
                          )}
                          {kind === "purchase" && r.destination === "site" && (
                            <span className="inline-flex items-center rounded-sm bg-teal-50 text-teal-700 border border-teal-200 px-1.5 py-[1px] text-[10px] font-semibold uppercase tracking-widest">SITE</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-600">{projMap[r.project_id] || "—"}</TableCell>
                      {kind === "purchase" ? (
                        <>
                          <TableCell className="text-right font-mono-num text-[11.5px] text-slate-600">
                            {lineCount > 1 ? (
                              <button
                                onClick={() => setExpanded(expanded === r.id ? null : r.id)}
                                className="text-[#EA580C] hover:underline font-semibold"
                                data-testid={`expand-lines-${r.id}`}
                              >
                                {lineCount} lines
                              </button>
                            ) : (
                              <span>1</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right font-mono-num">{fmtMoney(r.subtotal ?? r.total ?? 0)}</TableCell>
                          <TableCell className="text-right font-mono-num text-[11.5px] text-slate-600">
                            {r.tax_amount > 0 ? fmtMoney(r.tax_amount) : "—"}
                          </TableCell>
                          <TableCell className={`text-right font-mono-num font-semibold ${totalColor}`}>{fmtMoney(r.total)}</TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell className="text-right font-mono-num">{r.quantity}</TableCell>
                          <TableCell className="text-right font-mono-num">{fmtMoney(r.unit_price)}</TableCell>
                          <TableCell className={`text-right font-mono-num font-semibold ${totalColor}`}>{fmtMoney(r.total)}</TableCell>
                        </>
                      )}
                      <TableCell>
                        <div className="flex gap-1 justify-end">
                          {kind === "purchase" && (
                            <button onClick={() => setExpanded(expanded === r.id ? null : r.id)} data-testid={`expand-${testidPrefix}-${r.id}`} title="Details / attachments" className="text-slate-500 hover:text-[#EA580C] p-1"><Paperclip size={14} /></button>
                          )}
                          <button
                            onClick={() => setEditing(r)}
                            data-testid={`edit-${testidPrefix}-${r.id}`}
                            title="Edit"
                            className="text-slate-400 hover:text-[#EA580C] p-1"
                          ><PencilSimple size={14} /></button>
                          <button onClick={() => del(r.id)} data-testid={`delete-${testidPrefix}-${r.id}`} className="text-slate-400 hover:text-red-600 p-1"><Trash size={14} /></button>
                        </div>
                      </TableCell>
                    </TableRow>
                    {kind === "purchase" && expanded === r.id && (
                      <PurchaseRowExpanded row={r} />
                    )}
                  </React.Fragment>
                );
                })}
              </TableBody>
            </Table>
          </div>
        )}
        {isPurchase && totalCount > PAGE_SIZE && (
          <div className="flex items-center justify-between mt-4 text-sm" data-testid="purchases-pagination">
            <span className="text-slate-500">
              Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, totalCount)} of {totalCount.toLocaleString("en-IN")}
            </span>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" className="rounded-sm" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="purchases-prev-page">Prev</Button>
              <span className="px-2 text-slate-600">Page {page} / {Math.max(1, Math.ceil(totalCount / PAGE_SIZE))}</span>
              <Button variant="outline" size="sm" className="rounded-sm" disabled={page >= Math.ceil(totalCount / PAGE_SIZE)} onClick={() => setPage((p) => p + 1)} data-testid="purchases-next-page">Next</Button>
            </div>
          </div>
        )}
      </div>

      <AlertDialog open={importOpen} onOpenChange={setImportOpen}>
        <AlertDialogContent data-testid="import-history-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Import historical purchase register</AlertDialogTitle>
            <AlertDialogDescription>
              This loads the full Tally purchase register (FY 2025-26 and FY 2026-27 —
              about 1,651 invoices) into this environment. It is safe to run: invoices that
              already exist are skipped, so your current records are never changed or
              duplicated. Run once per environment.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="import-history-cancel" disabled={importing}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="import-history-confirm"
              disabled={importing}
              onClick={(e) => { e.preventDefault(); runHistoricalImport(); }}
            >
              {importing ? "Importing…" : "Import now"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export function Purchases() {
  return <Ledger kind="purchase" testidPrefix="purchases" title="Purchases" subtitle="Materials, equipment and vendor spend." actorLabel="Vendor" actorField="vendor" resource="purchases" />;
}

export function Sales() {
  return <Ledger kind="sale" testidPrefix="sales" title="Sales" subtitle="Direct sales revenue (non-invoiced)." actorLabel="Customer" actorField="customer" resource="sales" />;
}
